import type { ApiResponse, PaginatedResponse } from '@/types'

// 声明全局环境变量类型
declare global {
  interface Window {
    __ENV__?: {
      API_BASE_URL?: string
      CDN_URL?: string
      UPLOAD_API_BASE_URL?: string
    }
  }
}

// 获取环境变量配置
const getEnvConfig = () => {
  const windowEnv = typeof window !== 'undefined' ? window.__ENV__ || {} : {}
  return {
    API_BASE_URL: windowEnv.API_BASE_URL || import.meta.env.VITE_API_BASE_URL || '',
    CDN_URL: windowEnv.CDN_URL || import.meta.env.VITE_CDN_URL || '',
    UPLOAD_API_BASE_URL: windowEnv.UPLOAD_API_BASE_URL || import.meta.env.VITE_UPLOAD_API_BASE_URL || '',
  }
}

class ApiClient {
  private token: string | null = null
  // 多个并发请求同时 401 时,只允许第一个触发跳转,后续保持现状直到页面卸载。
  // 避免 N 个 401 触发 N 次 location.href 抢占,以及读 reason 的竞态。
  private redirecting = false

  constructor() {
    // 从localStorage获取token
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('admin_token')
    }
  }

  private getBaseURL(): string {
    return getEnvConfig().API_BASE_URL
  }

  private getUploadBaseURL(): string {
    const config = getEnvConfig()
    return config.UPLOAD_API_BASE_URL || config.API_BASE_URL
  }

  // 处理 401 未授权:清除 token 并跳转登录页。
  // reason 来自后端 401 响应里的 message,会被原样带到 /login?reason=...,登录页可识别后展示。
  private handleUnauthorized(reason?: string) {
    if (typeof window === 'undefined') return
    if (this.redirecting) return  // 第一个 401 已经触发跳转,后续并发的 401 静默丢弃
    this.redirecting = true

    // 清除本地 token 和用户信息(包括权限码,避免下次登录前菜单还残留)
    this.token = null
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user')
    localStorage.removeItem('admin_permissions')

    // 避免在登录页重复跳转(精确匹配,避免误判 /login-history 等路径)
    const pathname = window.location.pathname
    if (pathname !== '/login' && !pathname.startsWith('/login?')) {
      const currentPath = pathname + window.location.search
      const params = new URLSearchParams()
      params.set('redirect', currentPath)
      if (reason) params.set('reason', reason)
      window.location.href = `/login?${params.toString()}`
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.getBaseURL()}${endpoint}`
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }

    // 每次请求时都从localStorage获取最新的token
    const currentToken = typeof window !== 'undefined'
      ? localStorage.getItem('admin_token')
      : this.token

    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      // 401 未授权:token 过期/无效/被踢,清理本地态并跳转登录页。
      // 先尝试读后端给的 message 用于在登录页展示更具体的原因(单点登录被顶 / 改了角色 / 改了密码 等)。
      if (response.status === 401) {
        let reason: string | undefined
        try {
          const body = await response.clone().json()
          reason = typeof body?.message === 'string' ? body.message : undefined
        } catch { /* 后端可能返回非 JSON,忽略 */ }
        this.handleUnauthorized(reason)
        throw new Error(reason || '登录已过期，请重新登录')
      }

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  async get<T>(endpoint: string, options?: { params?: Record<string, unknown> | object }): Promise<ApiResponse<T>> {
    let url = endpoint
    if (options?.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url = `${endpoint}?${queryString}`
      }
    }
    return this.request<T>(url, { method: 'GET' })
  }

  // download 用于文件下载(CSV/Excel 等):带鉴权头、拼 baseURL,返回原始 Blob(不按 JSON 解析)。
  async download(endpoint: string, params?: Record<string, unknown> | object): Promise<Blob> {
    let url = endpoint
    if (params) {
      const sp = new URLSearchParams()
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) sp.append(k, String(v))
      })
      const qs = sp.toString()
      if (qs) url = `${endpoint}?${qs}`
    }
    const headers: Record<string, string> = {}
    const currentToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : this.token
    if (currentToken) headers.Authorization = `Bearer ${currentToken}`
    const response = await fetch(`${this.getBaseURL()}${url}`, { headers })
    if (!response.ok) throw new Error(`下载失败 (${response.status})`)
    return response.blob()
  }

  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  // 分页请求
  async getPaginated<T>(
    endpoint: string,
    params?: {
      page?: number
      page_size?: number
      [key: string]: unknown
    }
  ): Promise<ApiResponse<PaginatedResponse<T>>> {
    const searchParams = new URLSearchParams()

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value))
        }
      })
    }

    const url = searchParams.toString() ? `${endpoint}?${searchParams.toString()}` : endpoint
    return this.get<PaginatedResponse<T>>(url)
  }

  // 设置认证token
  setToken(token: string) {
    this.token = token
    if (typeof window !== 'undefined') {
      localStorage.setItem('admin_token', token)
    }
  }

  // 清除token
  clearToken() {
    this.token = null
    if (typeof window !== 'undefined') {
      localStorage.removeItem('admin_token')
    }
  }

  // 获取当前token
  getToken(): string | null {
    return this.token
  }

  // 文件上传方法
  async upload<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData()
    formData.append('file', file)

    // 添加额外的表单数据
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value)
      })
    }

    const isAbsolute = endpoint.startsWith('http')
    const isAdminUpload = endpoint.includes('/admin/files/upload')
    const base = isAdminUpload ? this.getUploadBaseURL() : this.getBaseURL()
    const url = isAbsolute ? endpoint : `${base}${endpoint}`
    const headers: Record<string, string> = {}

    // 获取token
    const currentToken = typeof window !== 'undefined'
      ? localStorage.getItem('admin_token')
      : this.token

    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      })

      // 401 未授权:token 过期/无效/被踢,清理本地态并跳转登录页。
      // 先尝试读后端给的 message 用于在登录页展示更具体的原因(单点登录被顶 / 改了角色 / 改了密码 等)。
      if (response.status === 401) {
        let reason: string | undefined
        try {
          const body = await response.clone().json()
          reason = typeof body?.message === 'string' ? body.message : undefined
        } catch { /* 后端可能返回非 JSON,忽略 */ }
        this.handleUnauthorized(reason)
        throw new Error(reason || '登录已过期，请重新登录')
      }

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error('Upload request failed:', error)
      throw error
    }
  }

  // 删除由独立 upload-api 保存的文件。调用前应先通过业务 API 检查文件是否仍被引用。
  async deleteUpload<T>(endpoint: string): Promise<ApiResponse<T>> {
    const url = `${this.getUploadBaseURL()}${endpoint}`
    const currentToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : this.token
    const response = await fetch(url, {
      method: 'DELETE',
      headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
    })
    if (response.status === 401) {
      this.handleUnauthorized()
      throw new Error('登录已过期，请重新登录')
    }
    const data = await response.json()
    if (!response.ok) throw new Error(data.message || `HTTP error! status: ${response.status}`)
    return data
  }
}

export const apiClient = new ApiClient()
export default apiClient
